// ─────────────────────────────────────────────────────────────────────────────
// OtpInput — segmented N-digit verification-code entry.
//
// Controlled: `value` is the full code string, `onChange` receives the new
// digits-only string (already trimmed to `length`). Behaviour:
//   • type a digit  → fills the box and advances to the next
//   • Backspace      → clears the box, or steps back and clears the previous
//   • ← / →          → move between boxes
//   • paste          → distributes a pasted code across all boxes
//   • browser / SMS autofill lands in box 0 and is distributed automatically
// ─────────────────────────────────────────────────────────────────────────────
import { useRef } from "react";
import styles from "../Auth.module.css";

export function OtpInput({
  value = "",
  onChange,
  length = 6,
  disabled = false,
  autoFocus = true,
}) {
  const inputs = useRef([]);
  const digits = Array.from({ length }, (_, i) => value[i] ?? "");

  const emit = (next) => onChange(next.replace(/\D/g, "").slice(0, length));

  const focusBox = (i) => {
    const el = inputs.current[i];
    if (el) { el.focus(); el.select?.(); }
  };

  const setDigit = (i, char) => {
    const arr = value.padEnd(length, " ").split("");
    arr[i] = char || " ";
    emit(arr.join("").replace(/\s/g, ""));
  };

  const handleChange = (i) => (e) => {
    const raw = e.target.value.replace(/\D/g, "");
    if (!raw) { setDigit(i, ""); return; }
    if (raw.length > 1) {
      // Multiple digits (paste / autofill into one box) — distribute from here.
      const merged = (value.slice(0, i) + raw);
      emit(merged);
      focusBox(Math.min(i + raw.length, length - 1));
      return;
    }
    setDigit(i, raw);
    if (i < length - 1) focusBox(i + 1);
  };

  const handleKeyDown = (i) => (e) => {
    if (e.key === "Backspace") {
      if (digits[i]) {
        setDigit(i, "");
      } else if (i > 0) {
        setDigit(i - 1, "");
        focusBox(i - 1);
      }
    } else if (e.key === "ArrowLeft" && i > 0) {
      e.preventDefault(); focusBox(i - 1);
    } else if (e.key === "ArrowRight" && i < length - 1) {
      e.preventDefault(); focusBox(i + 1);
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
    if (!pasted) return;
    e.preventDefault();
    emit(pasted);
    focusBox(Math.min(pasted.length, length - 1));
  };

  return (
    <div className={styles.otpRow} onPaste={handlePaste}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => { inputs.current[i] = el; }}
          className={styles.otpBox}
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          maxLength={1}
          value={d}
          onChange={handleChange(i)}
          onKeyDown={handleKeyDown(i)}
          onFocus={(e) => e.target.select()}
          disabled={disabled}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus && i === 0}
          aria-label={`Verification code digit ${i + 1}`}
        />
      ))}
    </div>
  );
}

export default OtpInput;
